import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

// Eager load LandingPage — it's the primary page every visitor hits
import LandingPage from "./components/LandingPage";

// Lazy load secondary routes for code splitting
const ProtectedCRM = lazy(() => import("./components/ProtectedCRM"));
const BlogHome = lazy(() => import("./components/BlogHome"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const Terms = lazy(() => import("./components/Terms"));
const SellHouseFastSarasotaFL = lazy(() => import("./components/SellHouseFastSarasotaFL"));
const SellHouseFastNorthPortFL = lazy(() => import("./components/SellHouseFastNorthPortFL"));
const SellHouseFastPortCharlotteFL = lazy(() => import("./components/SellHouseFastPortCharlotteFL"));
const SellHouseFastPuntaGordaFL = lazy(() => import("./components/SellHouseFastPuntaGordaFL"));
const SellHouseFastEnglewoodFL = lazy(() => import("./components/SellHouseFastEnglewoodFL"));
const TakeoverCalculator = lazy(() => import("./components/TakeoverCalculator"));
const SellHouseFastVeniceFL = lazy(() => import("./components/SellHouseFastVeniceFL"));
const SellHouseFastTampaFL = lazy(() => import("./components/SellHouseFastTampaFL"));

// Error Boundary — prevents blank page if React crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("React Error Boundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", fontFamily: "system-ui, sans-serif", padding: "20px", textAlign: "center" }}>
          <h2 style={{ color: "#16a34a", marginBottom: "12px" }}>Something went wrong</h2>
          <p style={{ color: "#555", marginBottom: "20px" }}>Please refresh the page or call us directly.</p>
          <a href="mailto:alex@enkohomebuyers.com" style={{ background: "#16a34a", color: "#fff", padding: "14px 32px", borderRadius: "8px", textDecoration: "none", fontSize: "18px", fontWeight: "bold" }}>Email alex@enkohomebuyers.com</a>
          <button onClick={() => window.location.reload()} style={{ marginTop: "16px", background: "none", border: "1px solid #ccc", padding: "10px 24px", borderRadius: "6px", cursor: "pointer", color: "#333" }}>Refresh Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading fallback for lazy routes
const PageLoader = () => (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: "40px", height: "40px", border: "4px solid #e5e7eb", borderTopColor: "#16a34a", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

import BACKEND_URL from "./apiConfig";

const API = `${BACKEND_URL}/api`;

const GA_MEASUREMENT_ID = 'G-35LVWZH76D';
const GADS_ID = 'AW-17611714161';

const TestBackendConnection = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      axios.get(`${API}/`).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  return null;
};

const GA4RouteTracker = () => {
  const location = useLocation();

  // Send page_view on route change (does NOT trigger a conversion — just navigation tracking)
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
        send_to: GA_MEASUREMENT_ID,
      });
    }
  }, [location]);

  // Global phone click handler — fires ONCE per click on any tel: link
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a[href^="tel:"]');
      if (link && typeof window.gtag === 'function') {
        const phoneNumber = link.getAttribute('href').replace('tel:', '');
        // Single event for phone clicks — import as conversion in Google Ads
        window.gtag('event', 'phone_call_click', {
          event_category: 'Lead',
          event_label: phoneNumber,
          page_path: window.location.pathname,
        });
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <TestBackendConnection />
        <BrowserRouter>
          <GA4RouteTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/admin" element={<ProtectedCRM />} />
              <Route path="/crm-dashboard" element={<ProtectedCRM />} />
              <Route path="/sell-house-fast-sarasota-fl" element={<SellHouseFastSarasotaFL />} />
              <Route path="/sell-house-fast-north-port-fl" element={<SellHouseFastNorthPortFL />} />
              <Route path="/sell-house-fast-port-charlotte-fl" element={<SellHouseFastPortCharlotteFL />} />
              <Route path="/sell-house-fast-punta-gorda-fl" element={<SellHouseFastPuntaGordaFL />} />
              <Route path="/sell-house-fast-englewood-fl" element={<SellHouseFastEnglewoodFL />} />
              <Route path="/mortgage-takeover-calculator" element={<TakeoverCalculator />} />
              <Route path="/sell-house-fast-venice-fl" element={<SellHouseFastVeniceFL />} />
              <Route path="/sell-house-fast-tampa-fl" element={<SellHouseFastTampaFL />} />
              <Route path="/blog" element={<BlogHome />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
